import React, { useState } from 'react';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import { useEffect } from 'react';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Grid,
  HeaderTemplate,
  Icon,
  SectionHeader,
  Stepper,
  TitleHeader,
  Typography,
} from 'web-library';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { IconName } from 'web-library/stories/atom/Icon/Icon';

import RouteKey from '../../../routes/key';
import DraftMessage from '../../../modules/specialConsideration/components/DraftMessage';
import SelectSubjectTable from '../../../modules/specialConsideration/components/SelectSubjectTable';
import AddSubjectModal from '../../../modules/specialConsideration/components/AddSubjectModal';
import { candidateService } from '../../../api';

const { specialConsideration: specialConsiderationRoute } = RouteKey.child;
const {
  specialConsideration,
  instructions,
  uploadSupportingDocument,
  amendUploadSupportingDocument,
  applicationDetails,
} = RouteKey['special-consideration'].child;

const useStyles = makeStyles()((theme) =>
({
  stepper: {
    position: 'relative',
    left: '-6.5%',
  },
  backButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  submitButton: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  buttonContainer: {
    marginBottom: theme.spacing(8),
  },
  iconReminder: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  keyValueContainer: {
    marginTop: theme.spacing(2),
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  subjectErrorMessage: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  addButtonContainer: {
    margin: theme.spacing(1),
    display: 'inline-flex',
    cursor: 'pointer',
    '&:hover': {
      '& $icon, & $button': {
        color: theme.color.link.linkHover01,
      },
      '& $iconRemove, $buttonRemove': {
        color: theme.color.link.linkHover02,
      },
    },
  },
  button: {
    margin: 'auto',
    padding: 10,
    minWidth: 0,
  },
  icon: {
    margin: 'auto',
  },
}));

interface SelectSubjectPageProps {
  title: string;
  routeSteps: { label: string }[];
  amendment: boolean;
}

const SelectSubject = ({ title, routeSteps, amendment }: SelectSubjectPageProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const { uuid } = useParams<{ uuid: string }>();
  const {
    data,
    isFetching,
    refetch: refetchSubjectTable,
  } = candidateService.specialConsideration.candidate.useGetSpecialConsiderationSubjectDetails(
    uuid
  );

  const [modalState, setModalState] = useState<boolean>(false);
  const [allowProceed, setAllowProceed] = useState(false);
  const [subjectError, setSubjectError] = useState<string | null>(null);

  const goBackClick = () => {
    if (amendment) {
      history.push(`${applicationDetails.route}`);
    } else {
      history.push(
        `/${specialConsiderationRoute}/${specialConsideration.route}/${instructions.route}`,
        {
          isContinueApplication: true,
          applicationUuid: uuid,
        }
      );
    }
  };

  const addSubjectClick = () => {
      if (data && data.length >= 1) {
          setSubjectError(t('components->>ApplicationReminderMessage.SubjectErrorText'));
          return;
      }
      setSubjectError(null);
      setModalState(true);
  };

useEffect(() => {
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = '';
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}, []);



  return (
    <>
    <Prompt
      when={!allowProceed}
      message={(location) => {
        const nextPath = location.pathname;

        if (
          nextPath.includes(uploadSupportingDocument.route) ||
          nextPath.includes(amendUploadSupportingDocument.route)
        ) {
          return true; // allow navigation without popup
        }
          return t("specialConsideration->>ApplyForSpecialConsideration.ReminderToSubmitPopUp");
      }}
    />
      <TitleHeader title={title} />
      <HeaderTemplate>
        <div className={classes.stepper}>
          <Stepper activeStep={0} steps={routeSteps} />
        </div>
      </HeaderTemplate>
      <ContentTemplate type="full">
        <Grid container direction="row" className={classes.headerContainer}>
          <SectionHeader
            title={
              amendment
                ? t('specialConsideration->>ApplyForSpecialConsideration.ViewSelectedSubject')
                : t('specialConsideration->>ApplyForSpecialConsideration.SelectSubjectPaper')
            }
          />
          {!amendment && (
            <Box className={classes.addButtonContainer}>
              <Icon
                size="M"
                className={classes.icon}
                name={IconName.add}
                color={theme.color.icon.iconSelected}
              />
              <Button
                id="button-addSubject"
                className={classes.button}
                onClick={addSubjectClick}
                variant="text"
                color="primary"
                disabled={isFetching}
              >
                {t('specialConsideration->>ApplyForSpecialConsideration.AddSubjectPaper')}
              </Button>
            </Box>
          )}
        </Grid>
        <Box>
        <SelectSubjectTable
          data={data || []}
          isFetching={isFetching}
          refetchSubjectTable={refetchSubjectTable}
          amendment={amendment}
          setSubjectError={setSubjectError}
        />
        {subjectError && (
         <Box display="flex" alignItems="center">
           <Icon
             size="M"
             name={IconName.info}
             className={classes.iconReminder}
             color={theme.color.icon.iconTrash}
           />
           <Typography className={classes.subjectErrorMessage} style={{color: theme.color.icon.iconTrash}}>
             {subjectError}
           </Typography>
         </Box>
        )}
        </Box>
        <Box className={classes.buttonContainer}>
          <Button
            id="button-select-subject-back"
            className={classes.backButton}
            variant="outlined"
            onClick={goBackClick}
          >
            {t('components->>CompleteProfileDetails.Back')}
          </Button>
          <ApiButton
            id="button-proceed"
            className={classes.submitButton}
            status="idle"
            type="submit"
            onClick={() => {
              setAllowProceed(true);
              history.push(
                amendment
                  ? `${amendUploadSupportingDocument.route}`
                  : `${uploadSupportingDocument.route}`
              );
            }}
            disabled={isFetching || (!data?.length && !amendment)}
          >
            {amendment
              ? t('specialConsideration->>ApplyForSpecialConsideration.ProceedWithSubjects')
              : t('specialConsideration->>ApplyForSpecialConsideration.ConfirmSubjectPapers')}
          </ApiButton>
          <DraftMessage />
        </Box>
      </ContentTemplate>
      {modalState && (
        <AddSubjectModal
          modalState={modalState}
          onDialogClose={() => setModalState(false)}
          refetchSubjectTable={refetchSubjectTable}
        />
      )}
    </>
  );
};

export default SelectSubject;
